import ComponentManager, {
	ComponentOptions,
	ComponentOptionsInterface,
	ComponentOptionsValuePlaceholder
} from "../../../componentFramework";
import EventListenerManager from "../../EventListenerManager";
import ExpandableManager, {ExpandableManagerEventData} from "../ExpandableManager";
import arraysHaveAtLeastOneCommonItem from "../../../scripts/arraysHaveAtLeastOneCommonItem";
import Tooltip from "../../Tooltip";

const defaultOptions = {
	styleClass: {
		isReady: "expandableControl--isReady",
		isExpanded: "expandableControl--isExpanded"
	},
	area: ComponentOptionsValuePlaceholder.array.required,
	group: ComponentOptionsValuePlaceholder.array.optional,
	// Query to find the element to search from for other ExpandableControl instances from.
	// This query is ran against rootElement using closest(). If not set use document.body.
	// This can also be set after construction using setSiblingsContainer()
	selectorForSiblingsContainer: ComponentOptionsValuePlaceholder.string.optional,
	// This can also be set after construction using setSelectorForSiblings()
	selectorForSiblings: ComponentOptionsValuePlaceholder.string.optional
};
export type DefaultOptionsType = typeof defaultOptions;


/**
 * See Expandable.
 */
@ComponentManager.register()
export default class ExpandableControl<InstanceOptions extends ComponentOptionsInterface = ComponentOptionsInterface> {
	public static componentStyleClass = "expandableControl";

	protected readonly options: ComponentOptions<DefaultOptionsType & InstanceOptions>;
	private readonly eventListenerManager = new EventListenerManager();

	private siblingsContainer: HTMLElement | null = null;
	private selectorForSiblings: string | null = null;

	constructor(protected readonly rootElement: HTMLElement, instanceOptions?: InstanceOptions) {
		this.options = new ComponentOptions(this, rootElement, defaultOptions).extend(instanceOptions);

		const managedExpandableManager = this.eventListenerManager.manage(ExpandableManager.getInstance());
		managedExpandableManager.addEventListener("expand", this.onManagerEvent);
		managedExpandableManager.addEventListener("collapse", this.onManagerEvent);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		// Listen for click and key press
		const controlManaged = this.eventListenerManager.manage(this.rootElement);
		controlManaged.addEventListener("click", (event: MouseEvent) => {
			this.expandEvent(event);
		});
		controlManaged.addEventListener("keydown", (event: KeyboardEvent) => {
			if (event.target === event.currentTarget) {
				if (event.key === "Enter") {
					this.expandEvent(event);
				} else if (event.code === "Space") {
					this.expandEvent(event);
					event.preventDefault();
				} else if (event.key === "ArrowRight") {
					this.expandEvent(event, true);
				} else if (event.key === "ArrowLeft") {
					this.expandEvent(event, false);
				} else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
					// Change focus
					const allControlsInGroup = this.getAllControlsInGroup(true);
					const index = event.key === "ArrowUp" ? allControlsInGroup.indexOf(this) - 1 : allControlsInGroup.indexOf(this) + 1;
					if (index >= 0 && index < allControlsInGroup.length) {
						allControlsInGroup[index]!.focus(); // We know that the index is guaranteed to be valid here
						event.stopPropagation();
						event.preventDefault();
					}
				}
			}
		});
		const spaceBlocker = (event: KeyboardEvent) => {
			if (event.target === event.currentTarget && event.code === "Space") {
				event.preventDefault();
			}
		};
		controlManaged.addEventListener("keydown", spaceBlocker);
		controlManaged.addEventListener("keypress", spaceBlocker);

		// Mark as ready which will let it start to animate using CSS transition
		requestAnimationFrame(() => {
			this.rootElement.classList.add(this.options.getValues().styleClass.isReady);
		});
	}

	focus() {
		this.rootElement.focus();
	}

	isExpanded(): boolean {
		return this.rootElement.classList.contains(this.options.getValues().styleClass.isExpanded);
	}
	setExpanded(isExpand: boolean, entireGroup: boolean = false) {
		const options = this.options.getValues();

		let area: string[];
		if (entireGroup) {
			area = [];
			const controlsInGroup = this.getAllControlsInGroup(false);
			controlsInGroup.forEach(item => {
				item.getAreaValues().forEach(areaValue => area.push(areaValue));
			});
		} else {
			area = Array.from(options.area as string[]); // Copy array
		}

		ExpandableManager.getInstance().do(isExpand ? "expand" : "collapse", area);
	}

	protected getAreaValues(): string[] {
		return this.options.getValues().area;
	}

	setTooltip(message: string | null) {
		const componentManager = ComponentManager.getInstance();
		if (message === null || message.length === 0) {
			this.rootElement.removeAttribute("title");
			componentManager.destroyComponentInstance(Tooltip, this.rootElement);
		} else {
			const tooltip = ComponentManager.getComponent(this.rootElement, Tooltip);
			if (tooltip !== undefined) {
				tooltip.setTooltipText(message);
			} else {
				this.rootElement.setAttribute("title", message);
				componentManager.createComponentInstance(Tooltip, this.rootElement);
			}
		}
	}

	private onManagerEvent = (data: ExpandableManagerEventData) => {
		let isExpand: boolean;
		if (data.type === "expand") {
			isExpand = true;
		} else if (data.type === "collapse") {
			isExpand = false;
		} else {
			return; // Nothing to do.
		}

		const options = this.options.getValues();
		if (arraysHaveAtLeastOneCommonItem(data.area, options.area)) {
			this.rootElement.classList.toggle(options.styleClass.isExpanded, isExpand);
		}
	}

	private expandEvent(event: {ctrlKey: boolean, shiftKey: boolean, metaKey: boolean}, isExpand = !this.isExpanded()) {
		const updateAllInGroupToMatch = event.ctrlKey || event.shiftKey || event.metaKey;
		this.setExpanded(isExpand, updateAllInGroupToMatch);
	}

	protected getRootElement(): HTMLElement {
		return this.rootElement;
	}

	protected isInGroup(group: string[]): boolean {
		const options = this.options.getValues();
		if (group === null && options.group === null) {
			return true;
		}
		return arraysHaveAtLeastOneCommonItem(group, options.group);
	}

	public setSiblingsContainer(container: HTMLElement | null) {
		this.siblingsContainer = container;
	}
	public setSelectorForSiblings(selector: string | null) {
		this.selectorForSiblings = selector;
	}

	private getAllControlsInGroup(order: boolean): Array<ExpandableControl> {
		const options = this.options.getValues();

		// Query for all ExpandableControl instances to consider
		let container: HTMLElement | null = null;
		if (this.siblingsContainer !== null) {
			container = this.siblingsContainer;
		} else if (options.selectorForContainer !== null) {
			container = this.rootElement.closest(options.selectorForSiblingsContainer);
		}
		if (container === null) {
			container = this.rootElement.ownerDocument.body;
		}

		let querySelector: string | undefined = undefined;
		if (this.selectorForSiblings !== null) {
			querySelector = this.selectorForSiblings;
		} else if (options.selectorForSiblings !== null) {
			querySelector = options.selectorForSiblings;
		}

		let controls = ComponentManager.queryForAllComponents(container, ExpandableControl, querySelector);

		// Filter to all in the same group
		controls = controls.filter(control => control.isInGroup(options.group));

		// Sort based tabindex and DOM order
		if (order) {
			controls.sort((a, b) => {
				const aElement = a.getRootElement();
				const bElement = b.getRootElement();
				if (aElement === bElement) {
					return 0;
				} else if (
					aElement.tabIndex > bElement.tabIndex
					|| aElement.compareDocumentPosition(bElement) & Node.DOCUMENT_POSITION_PRECEDING
				) {
					return 1;
				} else {
					return -1;
				}
			});
		}

		return controls;
	}

}
